/**
 * `useSubmitBrief` — TanStack mutation wrapping `ProjectBriefsService.submit`.
 *
 * Mirrors `useUpdateBrief`: identity (`projectId`, `briefId`) bound at hook
 * construction; consumer owns toast + navigation via `onSuccess` / `onError`.
 *
 * Cache strategy:
 *   - On success: `setQueryData(BRIEFS_QUERY_KEYS.detail(...), fresh)` —
 *     the submit response IS the new brief; no refetch needed.
 *   - Invalidate the project-scoped lists key so list views reflect the
 *     status change.
 *   - NEVER invalidate the detail key (would queue a refetch and flicker
 *     the editor mid-render).
 *
 * Error normalization: the `SUPPRESS_ERROR_NOTIFICATION_HEADER`
 * stops the global problem-details interceptor from emitting its own toast
 * so the page can render a contextual one. The `mutationFn` then extracts
 * RFC 7807 `detail` from the AxiosError response and re-throws as a plain
 * `Error(detail)`; if `detail` is missing it falls back to the original
 * AxiosError message. Non-Axios errors pass through verbatim.
 */
import {
  ProjectBriefsService,
  type BriefResponseDto,
} from '@inference.engine/workspace-api-client/web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

import { BRIEFS_QUERY_KEYS } from './query-keys';
import { briefActivityQueryOptions } from './activity-queries';

export interface UseSubmitBriefMutationOptions {
  projectId: string;
  briefId: string;
  onSuccess?: (fresh: BriefResponseDto) => void;
  onError?: (error: Error) => void;
}

/** Submit takes no body — kept as a named empty record so `useMutation` can */
/** type its variables without triggering `no-invalid-void-type`. */
export type SubmitBriefMutationVariables = Record<string, never>;

export const useSubmitBrief = ({
  projectId,
  briefId,
  onSuccess,
  onError,
}: UseSubmitBriefMutationOptions) => {
  const queryClient = useQueryClient();

  return useMutation<BriefResponseDto, Error, SubmitBriefMutationVariables>({
    mutationFn: async () => {
      try {
        const response = await ProjectBriefsService.submit({
          client: workspaceHttp,
          path: { platformProjectId: projectId, briefId },
          // Global problem-details interceptor would emit its own toast; the
          // page renders a contextual one instead. SDK headers type does not
          // include this key, hence the cast.
          // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
          headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' } as never,
        });

        if (!response.data) {
          throw new Error('Failed to submit brief');
        }

        return response.data;
      } catch (err) {
        if (axios.isAxiosError<{ detail?: string }>(err)) {
          // Generic narrows `data` as required, but real responses without a
          // body (e.g. 5xx with empty payload) yield undefined at runtime.
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          const detail = err.response?.data?.detail;
          if (detail) throw new Error(detail, { cause: err });
        }
        throw err;
      }
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(BRIEFS_QUERY_KEYS.detail(projectId, briefId), fresh);
      void queryClient.invalidateQueries({
        queryKey: BRIEFS_QUERY_KEYS.lists(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: briefActivityQueryOptions({ projectId, briefId }).queryKey,
      });
      onSuccess?.(fresh);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
